import { useEffect, useState, type ReactElement } from 'react';
import { api } from '../api';

/**
 * "wird aufbereitet…" means one of two very different things: this takes a
 * while, or nothing is running at all. The app knows which -- the worker
 * writes a heartbeat -- so it should say so where the person is waiting,
 * instead of leaving them to watch a spinner for a night.
 */
export interface WorkerLastError {
  at: string;
  message: string;
  /** The message's own detail lines, one per missing setting. Never a stack. */
  error: string;
}

export function useWorkerAlive(watch: boolean): { alive: boolean | null; lastError: WorkerLastError | null } {
  const [state, setState] = useState<{ alive: boolean | null; lastError: WorkerLastError | null }>({ alive: null, lastError: null });

  useEffect(() => {
    if (!watch) return;
    let stopped = false;
    const look = async () => {
      try {
        const health = await api.workerHealth();
        if (!stopped) {
          setState({ alive: health.alive, lastError: health.lastError });
        }
      } catch {
        // Not knowing is not the same as knowing it is down; say nothing.
        if (!stopped) setState({ alive: null, lastError: null });
      }
    };
    void look();
    const timer = setInterval(() => void look(), 20000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [watch]);

  return state;
}

export function WorkerWarning({ alive, lastError }: { alive: boolean | null; lastError: WorkerLastError | null }): ReactElement | null {
  if (alive !== false) return null;
  return (
    <div className="card" style={{ margin: '0 0 14px', borderColor: 'var(--danger)' }} data-worker-down>
      <div className="spread">
        <div>
          <div className="title">Die Aufbereitung läuft gerade nicht</div>
          <div className="sub">
            Kein Arbeitsdienst meldet sich. Hochgeladenes bleibt so lange auf „wird aufbereitet…“ stehen und läuft von
            selbst weiter, sobald er wieder da ist — es geht nichts verloren.
          </div>
        </div>
        <span className="badge err">steht</span>
      </div>
      {/* The worker's own words are English and technical. The sentence in
          front of them is not -- and the lines below it are `pre-line`,
          because a refused start names one missing setting per line and
          collapsing them into a paragraph loses exactly the useful part. */}
      {lastError ? (
        <div className="sub" style={{ marginTop: 8, color: 'var(--err-text)' }} data-worker-error>
          {lastError.message === 'worker refused to start'
            ? 'Start abgelehnt, eine Einstellung fehlt oder ist zu kurz:'
            : `Zuletzt: ${lastError.message}`}
          <div style={{ whiteSpace: 'pre-line', marginTop: 4 }} data-worker-error-detail>
            {lastError.error}
          </div>
        </div>
      ) : null}
      <p className="hint" style={{ marginBottom: 0 }}>
        Für Admins: unter <b>Verwaltung → Diagnose</b> steht mehr. Auf dem Server ist es der Container <code>worker</code>.
      </p>
    </div>
  );
}
